import React, { useEffect, useState, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import axiosInstance from "../services/axiosInstance";
import { useRef } from "react";
import { handleApiError, handleApiSuccess } from "./apiErrorHandler";

/**
 * Props:
 * - visible (bool): whether dialog is visible
 * - onHide (func): called when dialog should close
 * - order (object): order object returned from /api/inventory/move-order/{id}/
 * - onSuccess (func): optional callback after successful payment (should refresh order)
 */
export default function RecordPaymentDialog({ visible, onHide, order, onSuccess }) {
  const [actionLoading, setActionLoading] = useState(false);

  // invoice selection if invoices present (for credit flows)
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  // supplier invoice is required only when order.receive_on_credit === true
  // payment amount auto-populated from order total
  const computeOrderTotal = (ord) => {
    if (!ord) return 0;
    if (ord.total_purchase_price != null) return Number(ord.total_purchase_price);
    // fallback: sum item.line_total_cost if present (MoveOrderDetailDialog uses line_total_cost)
    if (Array.isArray(ord.items) && ord.items.length) {
      const hasLineTotal = ord.items.some(it => it.line_total_cost != null && Number(it.line_total_cost) !== 0);
      if (hasLineTotal) {
        return ord.items.reduce((s, it) => s + (Number(it.line_total_cost ?? 0) || 0), 0);
      }
      // fallback to quantity * unit price if line_total_cost not provided
      return ord.items.reduce((s, it) => {
        const qty = Number(it.received_quantity ?? it.quantity ?? 0);
        const price = Number(it.unit_price ?? it.price ?? it.cost ?? 0);
        return s + (qty * price);
      }, 0);
    }
    return 0;
  };

  const computeDefaultAmount = (ord) => {
    if (!ord) return 0;
    if (ord.outstanding_balance != null) {
      return Number(ord.outstanding_balance);
    }
    const total = computeOrderTotal(ord);
    const paid = Number(ord.total_paid || 0);
    return Math.max(0, total - paid);
  };

  const defaultAmount = useMemo(() => computeDefaultAmount(order), [order]);

  const [amount, setAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [remarks, setRemarks] = useState("");

  const paymentMethods = [
    { label: "Cash", value: "cash" },
    { label: "Cheque", value: "cheque" },
    { label: "Card", value: "card" },
    { label: "Other", value: "other" },
  ];

    useEffect(() => {
        if (visible) {
        setAmount(defaultAmount ?? 0);
        setRemarks("");
        setPaymentMethod(null);
        // preselect first invoice if exists (helpful but not mandatory unless credit)
        if (order?.supplier_invoices && order.supplier_invoices.length) {
            setSelectedInvoiceId(order.supplier_invoices[0].id);
        } else {
            setSelectedInvoiceId(null);
        }
        }
    }, [visible, defaultAmount]);

  const validate = () => {
    if (!order) {
      handleApiError({ Error: "Order not loaded" });
      return false;
    }
    if (!amount || Number(amount) <= 0) {
      handleApiError({Error: "Payment amount must be positive"});
      return false;
    }
    // invoice mandatory for credit purchases
    if (order.receive_on_credit && !selectedInvoiceId) {
      handleApiError({Error: "Supplier invoice is required for credit purchases"});
      return false;
    }
    // require payment method for auditability
    if (!paymentMethod) {
      handleApiError({Error: "Please select a payment method"});
      return false;
    }
    return true;
  };

  const submitPayment = async () => {
    if (!validate()) return;
    setActionLoading(true);
    try {
      // endpoint name based on backend modifications; adjust if your route differs
      const url = `/api/inventory/move-order/${order.id}/record_payment/`;
      const payload = {
        invoice_id: selectedInvoiceId || null,
        amount,
        payment_method: paymentMethod,
        remarks,
      };

      const res = await axiosInstance.post(url, payload);
      handleApiSuccess("Payment recorded");
      onHide && onHide();
      onSuccess && onSuccess(res.data);
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const footer = (
    <div className="flex gap-2 justify-end">
      <Button label="Cancel" className="p-button-secondary" onClick={() => onHide && onHide()} disabled={actionLoading} outlined />
      <Button label="Record Payment" icon="pi pi-money-bill" onClick={submitPayment} loading={actionLoading} />
    </div>
  );

  return (
    <>
      <Dialog header="Record Payment" visible={visible} style={{ width: "520px" }} onHide={() => onHide && onHide()} footer={footer}>
        <div className="p-fluid">
          {/* Invoice select (optional except for credit) */}
          {order?.supplier_invoices && order.supplier_invoices.length > 0 && (
            <div className="field">
              <label>Supplier Invoice (optional)</label>
              <Dropdown
                value={selectedInvoiceId}
                options={order.supplier_invoices.map(inv => ({ label: `${inv.invoice_number || ('#' + inv.id)} - ${inv.outstanding_amount ?? inv.total_amount}`, value: inv.id }))}
                onChange={(e) => setSelectedInvoiceId(e.value)}
                placeholder="Select invoice (optional)"
              />
              {order.receive_on_credit && <small className="p-error">Supplier invoice is required for credit purchases</small>}
            </div>
          )}

          {/* If no invoices present but order is credit, show a notice that invoice is required */}
          {order?.receive_on_credit && (!order.supplier_invoices || order.supplier_invoices.length === 0) && (
            <div className="field">
              <label className="p-error">This is a credit purchase — a supplier invoice must exist. Create invoice during receive.</label>
            </div>
          )}

          <div className="field">
            <label>Amount</label>
            <InputNumber value={amount} onValueChange={(e) => setAmount(e.value)} mode="currency" currency="LKR" locale="en-US" showButtons />
            <small className="p-text-secondary">Defaults to current balance; edit if making partial or custom payment.</small>
          </div>

          <div className="field">
            <label>Payment Method</label>
            <Dropdown value={paymentMethod} options={paymentMethods} onChange={(e) => setPaymentMethod(e.value)} placeholder="Select method" />
          </div>

          <div className="field">
            <label>Remarks</label>
            <InputTextarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} />
          </div>
        </div>
      </Dialog>
    </>
  );
}
